import { Add, Delete, Downloading, FileDownload, Payment, Print, Search } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import { generatePDF } from "../../components/order/export_pdf";
import PaymentModal from "../../components/order/PaymentModal";
import PaymentReceiptModal from "../../components/order/PaymentReceiptModal";
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid } from '../../helpers/formatters';
import {
    buildOrdersListQuery,
    buildOrdersSummaryQuery,
    defaultOrdersDateRange,
    EMPTY_ORDER_SUMMARY,
    formatOrdersPeriodLabel,
    normalizeOrdersDateRange,
    ordersDateRangeForApi,
} from '../../helpers/orders';
import {
    downloadOrdersSalesReportCsv,
    fetchAllOrdersForReport,
} from '../../helpers/ordersSalesReportCsv';
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';

function OrdersPage() {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const location = useLocation(); // Para acessar parâmetros da URL
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [urlSearchTerm, setUrlSearchTerm] = useState('');
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [paymentReceiptModal, setPaymentReceiptModal] = useState({ open: false, order: null });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loadingPDF, setLoadingPDF] = useState(false);
    const defaultDateRange = useMemo(() => defaultOrdersDateRange(), []);
    const [draftDateFrom, setDraftDateFrom] = useState(defaultDateRange.from);
    const [draftDateTo, setDraftDateTo] = useState(defaultDateRange.to);
    const [draftSalesChannelId, setDraftSalesChannelId] = useState('');
    const [draftSalesChannelName, setDraftSalesChannelName] = useState('');
    const [filters, setFilters] = useState({
        from: defaultDateRange.from,
        to: defaultDateRange.to,
        salesChannelId: '',
        salesChannelName: '',
    });
    const [summary, setSummary] = useState(EMPTY_ORDER_SUMMARY);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);

    const handlePrintReceipt = (order, storeName = "") => {
        setLoadingPDF(true);
        generatePDF(order);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000)
    };

    const handleCreateCheckout = () => {
        navigate('/admin/register-checkout');  // Redireciona para a página de criação de material
    };

    // useEffect para capturar parâmetros de busca da URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchParam = urlParams.get('search');
        if (searchParam) {
            setSearchTerm(searchParam);
            setActiveSearchQuery(searchParam);
            setUrlSearchTerm(searchParam);
            setAppliedSearchTerm(true);
        }
    }, [location.search]);

    const fetchSummary = useCallback(() => {
        setSummaryLoading(true);
        const summaryQuery = buildOrdersSummaryQuery({
            q: activeSearchQuery,
            from: filters.from,
            to: filters.to,
            salesChannelId: filters.salesChannelId,
        });
        return api.get(`orders/summary?${summaryQuery}`)
            .then((response) => {
                setSummary(response.data || EMPTY_ORDER_SUMMARY);
            })
            .catch(() => {
                setSummary(EMPTY_ORDER_SUMMARY);
            })
            .finally(() => {
                setSummaryLoading(false);
            });
    }, [activeSearchQuery, filters.from, filters.to, filters.salesChannelId]);

    const fetchOrders = useCallback(() => {
        setIsLoading(true);
        const listQuery = buildOrdersListQuery({
            page: currentPage,
            limit: itemsPerPage,
            q: activeSearchQuery,
            from: filters.from,
            to: filters.to,
            salesChannelId: filters.salesChannelId,
        });
        api.get(`orders?${listQuery}`)
            .then(response => {
                setOrders(response.data.orders);
                setTotalPages(response.data.total_pages);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, filters.from, filters.to, filters.salesChannelId]);

    useListFetch(fetchOrders, [currentPage, itemsPerPage, activeSearchQuery, filters.from, filters.to, filters.salesChannelId]);
    useListFetch(fetchSummary, [activeSearchQuery, filters.from, filters.to, filters.salesChannelId]);

    const handleSearch = () => {
        setCurrentPage(1);
        setActiveSearchQuery(searchTerm.trim());
        setAppliedSearchTerm(Boolean(searchTerm.trim()));
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setActiveSearchQuery('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleDraftDateFromChange = (value) => {
        setDraftDateFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeOrdersDateRange(value, draftDateTo || value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleDraftDateToChange = (value) => {
        setDraftDateTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeOrdersDateRange(draftDateFrom || value, value);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
    };

    const handleApplyFilters = () => {
        const range = ordersDateRangeForApi(draftDateFrom, draftDateTo);
        if (range.error) {
            Swal.fire('Período inválido', range.error, 'warning');
            return;
        }
        const normalized = normalizeOrdersDateRange(range.from, range.to);
        setDraftDateFrom(normalized.from);
        setDraftDateTo(normalized.to);
        setFilters({
            from: normalized.from,
            to: normalized.to,
            salesChannelId: draftSalesChannelId || '',
            salesChannelName: draftSalesChannelId ? draftSalesChannelName : '',
        });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const next = defaultOrdersDateRange();
        setDraftDateFrom(next.from);
        setDraftDateTo(next.to);
        setDraftSalesChannelId('');
        setDraftSalesChannelName('');
        setFilters({
            from: next.from,
            to: next.to,
            salesChannelId: '',
            salesChannelName: '',
        });
        setCurrentPage(1);
    };

    const handleExportCsv = async () => {
        if (exportingCsv) {
            return;
        }
        setExportingCsv(true);
        try {
            const allOrders = await fetchAllOrdersForReport(api, {
                q: activeSearchQuery,
                from: filters.from,
                to: filters.to,
                salesChannelId: filters.salesChannelId,
            });
            if (!allOrders.length) {
                await Swal.fire({
                    icon: 'info',
                    title: 'Nenhuma venda',
                    text: 'Não há vendas no período/filtros atuais para exportar.',
                });
                return;
            }
            const stamp = `${filters.from || 'inicio'}_${filters.to || 'fim'}`;
            downloadOrdersSalesReportCsv(allOrders, `relatorio-vendas-${stamp}.csv`);
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao exportar',
                text: 'Não foi possível gerar o relatório CSV. Tente novamente.',
            });
        } finally {
            setExportingCsv(false);
        }
    };

    const periodLabel = formatOrdersPeriodLabel(filters.from, filters.to);
    const monthEndStr = defaultDateRange.to;

    const handleDeleteOrder = (orderId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta venda será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`orders/${orderId}`)
                        .then(response => {
                            setLoadingDelete(false);
                            // Atualiza o estado após remoção
                            setOrders(orders.filter(order => order.id !== orderId));
                            fetchSummary();
                            Swal.fire('Removido!', 'Venda removida com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover a venda:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover a venda.', 'error');
                        });
                }
            }
        });
    };

    const handleRegisterPayment = (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (order) {
            setPaymentReceiptModal({ open: true, order });
        }
    };

    const handleClosePaymentReceiptModal = (success) => {
        setPaymentReceiptModal({ open: false, order: null });

        if (success) {
            fetchOrders();
            fetchSummary();
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Vendas finalizadas</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Vendas
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar por cliente"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {/* Botão de venda */}
                    <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateCheckout}
                            startIcon={<Add />}
                            fullWidth={isMobile}
                        >
                            Venda
                        </Button>
                    </Box>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Filtros das vendas
                </Typography>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateFrom}
                            onChange={(e) => handleDraftDateFromChange(e.target.value)}
                            inputProps={{ max: draftDateTo || monthEndStr }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateTo}
                            onChange={(e) => handleDraftDateToChange(e.target.value)}
                            inputProps={{
                                min: draftDateFrom || undefined,
                                max: monthEndStr,
                            }}
                            disabled={!draftDateFrom}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SalesChannelSelect
                            label="Canal de vendas"
                            size="small"
                            value={draftSalesChannelId}
                            allowEmpty
                            emptyLabel="Todos os canais"
                            onChange={(e, channel) => {
                                const nextId = e.target.value;
                                setDraftSalesChannelId(nextId);
                                setDraftSalesChannelName(nextId ? (channel?.name || '') : '');
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Button variant="contained" onClick={handleApplyFilters}>
                                Aplicar filtros
                            </Button>
                            <Button variant="outlined" onClick={handleResetFilters}>
                                Mês atual
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleExportCsv}
                                disabled={exportingCsv}
                                startIcon={exportingCsv ? <CircularProgress size={16} /> : <FileDownload />}
                            >
                                {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
                            </Button>
                            {periodLabel && (
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={periodLabel}
                                />
                            )}
                            {filters.salesChannelName && (
                                <Chip
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    label={filters.salesChannelName}
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total de vendas
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading ? <CircularProgress size={28} /> : (summary.total_sales ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total de produtos
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading ? <CircularProgress size={28} /> : (summary.total_products ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Ticket médio
                            </Typography>
                            <Typography variant="h4">
                                {summaryLoading
                                    ? <CircularProgress size={28} />
                                    : formatCurrencyInCents(summary.average_ticket ?? 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Top 3 produtos
                            </Typography>
                            {summaryLoading ? (
                                <CircularProgress size={28} />
                            ) : (summary.top_products || []).length > 0 ? (
                                (summary.top_products || []).map((product, index) => (
                                    <Typography key={`${product.name}-${index}`} variant="body2">
                                        {index + 1}. {product.name}: {product.quantity} un.
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhum produto no período
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Alerta de destaque quando há busca por URL */}
            {urlSearchTerm && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Chip
                            label={`Pedido: ${urlSearchTerm}`}
                            color="warning"
                            size="small"
                        />
                    }
                >
                    Destaque aplicado ao pedido pesquisado. O pedido correspondente será destacado na lista abaixo.
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Produtos</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pagamento</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data da venda</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(orders) && orders.length > 0 && orders.map((order) => {
                            const isHighlighted = urlSearchTerm && formatUuid(order.id) === urlSearchTerm;
                            return (
                                <TableRow
                                    key={order.id}
                                    sx={(theme) => ({
                                        backgroundColor: isHighlighted
                                            ? theme.palette.warning.light + '40' // Destaque amarelo com transparência
                                            : !order.payment_details.paid
                                                ? theme.palette.mode === 'dark'
                                                    ? theme.palette.action.hover
                                                    : theme.palette.action.selected
                                                : 'inherit',
                                        border: isHighlighted
                                            ? `2px solid ${theme.palette.warning.main}`
                                            : 'none',
                                        '&:hover': {
                                            backgroundColor: isHighlighted
                                                ? theme.palette.warning.light + '60' // Destaque mais forte no hover
                                                : theme.palette.action.hover,
                                        },
                                        transition: 'all 0.3s ease', // Animação suave para todas as propriedades
                                        transform: isHighlighted ? 'scale(1.01)' : 'scale(1)', // Leve aumento no tamanho
                                        boxShadow: isHighlighted
                                            ? `0 4px 8px ${theme.palette.warning.main}40`
                                            : 'none',
                                    })}
                                >
                                    <TableCell>
                                        <Link
                                            to={`/admin/orders/${order.id}`}
                                            style={{
                                                color: theme.palette.secondary.main,
                                                textDecoration: 'none',
                                                fontWeight: 'bold',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {formatUuid(order.id)}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {order.customer.name}
                                        <br />
                                        Canal: {order.sales_channel.name}
                                    </TableCell>
                                    <TableCell>
                                        {order.products.map((product, index) => (
                                            <div key={index}>
                                                - {product.name}: {product.quantity} x {formatCurrencyInCents(product.base_price)}
                                            </div>
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        Bruto: <strong>{formatCurrencyInCents(order.amount)}</strong>
                                        <br />
                                        Líquido esperado:{' '}
                                        <strong>
                                            {formatCurrencyInCents(
                                                order.expected_net != null ? order.expected_net : order.amount
                                            )}
                                        </strong>
                                        <br />
                                        Forma: {(order.payment_methods || []).map((m) => m.name).filter(Boolean).join(' + ') || '—'}
                                        <br />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Conciliação:
                                            {order.bank_reconciled ? (
                                                <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                                    Bancária
                                                </Typography>
                                            ) : order.payment_received ? (
                                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                                    Realizada
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                                    Pendente
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatDate(order.created_at)}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Emitir recibo">
                                            <IconButton
                                                color="secondary"
                                                onClick={() => handlePrintReceipt(order)} // Chama a função de gerar o PDF
                                                aria-label="Comprovante"
                                                disabled={loadingPDF}
                                            >
                                                {loadingPDF ? <Downloading /> : <Print />}
                                            </IconButton>
                                        </Tooltip>
                                        {!order.payment_received && (
                                            <Tooltip title="Registrar recebimento">
                                                <IconButton
                                                    color="success"
                                                    onClick={() => handleRegisterPayment(order.id)}
                                                    aria-label="Registrar recebimento"
                                                >
                                                    <Payment />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteOrder(order.id)}
                                            aria-label="remover venda"
                                        >
                                            <Delete />
                                        </IconButton>
                                        {!order.payment_details.paid &&
                                            <PaymentModal id={order.id} />
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>

            {/* Modal de Registro de Recebimento */}
            <PaymentReceiptModal
                open={paymentReceiptModal.open}
                onClose={handleClosePaymentReceiptModal}
                order={paymentReceiptModal.order}
            />
        </div>
    );
}

export default OrdersPage;
